import { PhBooks, PhGraduationCap, PhStack } from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type { Store } from '@warp-drive/core';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import { executeSearchRequest } from 'tio-data/requests/search';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { buildApprovalQueueSearch } from 'tio-employee/utils/tas/approval-queue-search';
import type { TaskUrgency } from 'tio-employee/utils/dashboard-kit/attention';
import type {
  AttentionItem,
  AttentionSource,
  DashboardContext,
} from 'tio-employee/utils/partner-dashboard/registry';

/** How many of the oldest waiting items the dashboard lists before "N more". */
export const QUEUE_PREVIEW_SIZE = 5;

/** Waiting this many days or more is urgent: the API's SLA at-risk mark. */
export const URGENT_AFTER_DAYS = 14;

/** Waiting this many days or more is due soon: the approval queue's aged-item mark. */
export const SOON_AFTER_DAYS = 5;

const DAY_MS = 24 * 60 * 60 * 1000;

/** One pending item, reduced to what the dashboard shows and links to. */
export interface QueueItem {
  /** The queue document id. */
  id: string;
  employeeName: string;
  kind: 'application' | 'program';
  /** The review page a queue row's "View" button opens. */
  reviewRoute: string;
  reviewModel: string;
  /** ISO timestamp the item started waiting; drives age and ordering. */
  waitingSince?: string;
}

/** Everything the queue widgets and the attention source show, from one pair of searches. */
export interface QueueSnapshot {
  waitingOnYou: {
    total: number;
    /** Up to `QUEUE_PREVIEW_SIZE` items, oldest first. */
    oldest: QueueItem[];
  };
  /** Items the viewer could review that are waiting on someone else first. */
  waitingOnOthers: number;
}

interface QueueDocument {
  id: string;
  recordType?: string;
  employeeNameKeyword?: string;
  tasApplicationIdKeyword?: string;
  tasProgramInstanceIdKeyword?: string;
  tasApplicationAttentionPriorityDate?: string;
  tasProgramInstanceAttentionPriorityDate?: string;
  tasApplicationLatestSubmissionTs?: string;
  tasProgramInstanceLatestSubmissionTs?: string;
}

// Same rule as the queue row: instance rows open program approval even when they carry an application.
function toQueueItem(doc: QueueDocument): QueueItem {
  const isProgram = doc.recordType === 'instance' || !doc.tasApplicationIdKeyword;
  return {
    id: doc.id,
    employeeName: doc.employeeNameKeyword ?? '',
    kind: isProgram ? 'program' : 'application',
    reviewRoute: isProgram
      ? 'authenticated.admin.tas.programs'
      : 'authenticated.admin.tas.applications.approval',
    reviewModel: (isProgram ? doc.tasProgramInstanceIdKeyword : doc.tasApplicationIdKeyword) ?? '',
    waitingSince:
      doc.tasApplicationAttentionPriorityDate ||
      doc.tasProgramInstanceAttentionPriorityDate ||
      doc.tasApplicationLatestSubmissionTs ||
      doc.tasProgramInstanceLatestSubmissionTs,
  };
}

const snapshots = new WeakMap<DashboardContext, Promise<QueueSnapshot>>();

/**
 * The viewer's approval queue for this dashboard visit: the oldest items
 * waiting on them, how many there are, and how many wait on someone else.
 * Runs the same search as `/admin/tas/approval-queue`, so the counts match.
 * Memoized per context, so every widget and source on one visit shares a
 * single pair of requests.
 *
 * @throws (rejects) when either search request fails
 */
export function loadQueueSnapshot(ctx: DashboardContext): Promise<QueueSnapshot> {
  const cached = snapshots.get(ctx);
  if (cached) return cached;

  // A plain store search, not ApprovalQueueRefreshService: that service drains the queue page's
  // just-approved markers and may block reconciling them.
  const store = ctx.owner.lookup('service:store') as Store;
  const search = async (query: Record<string, unknown>) =>
    (await store.request<OpenSearchResponse>(executeSearchRequest(query))).content;
  const base = { companyId: ctx.companyId, reviewerId: ctx.employeeId || '-1' };
  const snapshot = Promise.all([
    search(
      buildApprovalQueueSearch({ ...base, order: 'first_submitted_asc', limit: QUEUE_PREVIEW_SIZE })
    ),
    search(buildApprovalQueueSearch({ ...base, waitingOnOthers: true, limit: 0 })),
  ]).then(([mine, others]) => {
    const page = OpenSearchResponseBuilder.from(mine).toPaginatedResult();
    return {
      waitingOnYou: {
        total: page.total,
        oldest: (page.items as QueueDocument[]).map(toQueueItem),
      },
      waitingOnOthers: OpenSearchResponseBuilder.from(others).toPaginatedResult().total,
    };
  });
  snapshots.set(ctx, snapshot);
  return snapshot;
}

/** Whole days since `iso`, or null when it is missing or unparseable. */
export function daysWaiting(iso: string | undefined, now = Date.now()): number | null {
  const at = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(at) ? null : Math.max(0, Math.floor((now - at) / DAY_MS));
}

/** Maps how long an item has waited to the attention row's urgency. */
export function urgencyForWaitingDays(days: number | null): TaskUrgency {
  if (days === null) return 'onTrack';
  if (days >= URGENT_AFTER_DAYS) return 'urgent';
  if (days >= SOON_AFTER_DAYS) return 'soon';
  return 'onTrack';
}

/**
 * Needs Your Attention rows for the approval queue: the oldest items waiting
 * on the viewer, each opening its review page, plus one row pointing to the
 * full queue when more are waiting. Account owners and partner admins are
 * included because they are the TAS fallback approvers; with nothing routed
 * to them the source contributes no rows.
 */
export const APPROVAL_QUEUE_SOURCE: AttentionSource = {
  id: 'approval-queue',
  drivenBy: ['approver', 'supervisor', 'accountOwner', 'partnerAdmin'],
  load: async (ctx) => {
    const intl = ctx.owner.lookup('service:intl') as IntlService;
    const { waitingOnYou } = await loadQueueSnapshot(ctx);

    const rows: AttentionItem[] = waitingOnYou.oldest.map((item) => {
      const days = daysWaiting(item.waitingSince);
      return {
        id: `approval-queue:${item.id}`,
        title: intl.t(`partner_dashboard.queue.review_${item.kind}`, { name: item.employeeName }),
        description: intl.t(`partner_dashboard.queue.review_${item.kind}_description`),
        icon: item.kind === 'program' ? PhGraduationCap : PhBooks,
        urgency: urgencyForWaitingDays(days),
        dueAt: item.waitingSince,
        ...(days === null
          ? {}
          : { dueLabel: intl.t('partner_dashboard.queue.waiting_days', { count: days }) }),
        action: {
          label: intl.t('partner_dashboard.queue.review_action'),
          route: item.reviewRoute,
          model: item.reviewModel,
        },
        pii: true,
      };
    });

    const more = waitingOnYou.total - waitingOnYou.oldest.length;
    if (more > 0) {
      rows.push({
        id: 'approval-queue:more',
        title: intl.t('partner_dashboard.queue.more_title', { count: more }),
        description: intl.t('partner_dashboard.queue.more_description'),
        icon: PhStack,
        urgency: 'info',
        action: {
          label: intl.t('partner_dashboard.queue.open_queue'),
          route: 'authenticated.admin.tas.approval-queue',
        },
      });
    }
    return rows;
  },
};
